import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { summarize } from '@/lib/writer/telemetrySummarize';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// Vertex AI Gemini 3.5 Flash indicative list price (EU multi-region).
// Adjust here as pricing shifts; kept as constants so the panel is auditable.
const COST_PER_1M_INPUT_USD = 0.3;
const COST_PER_1M_OUTPUT_USD = 2.5;
const USD_TO_EUR = 0.92;
const TARGET_GROSS_MARGIN = 0.7; // 70% margin target

interface Row {
  id: string;
  created_at: string;
  kind: string;
  turns: number;
  credit_equivalent: number;
  project_id: string | null;
  thread_id: string | null;
  metadata: Record<string, unknown> | null;
}

export function computeSuggestion(rows: Row[]) {
  const s = summarize(rows);
  if (s.totals.turns === 0) return null;

  // Assume 70/30 input/output split when we don't have separate averages.
  // The ledger already splits when the model provides it — use those when available.
  const promptAvg = s.totals.turns
    ? s.totals.promptTokens / s.totals.turns
    : 0;
  const completionAvg = s.totals.turns
    ? s.totals.completionTokens / s.totals.turns
    : 0;
  const fallbackTotal = s.avg.totalTokensPerTurn;
  const promptTokens = promptAvg > 0 ? promptAvg : fallbackTotal * 0.7;
  const completionTokens = completionAvg > 0 ? completionAvg : fallbackTotal * 0.3;

  const costUsd =
    (promptTokens / 1_000_000) * COST_PER_1M_INPUT_USD +
    (completionTokens / 1_000_000) * COST_PER_1M_OUTPUT_USD;
  const costEur = costUsd * USD_TO_EUR;
  const suggestedPriceEur = costEur / (1 - TARGET_GROSS_MARGIN);

  return {
    sampleSize: s.totals.turns,
    avgPromptTokens: promptTokens,
    avgCompletionTokens: completionTokens,
    costEurPerTurn: costEur,
    suggestedPriceEurPerTurn: suggestedPriceEur,
  };
}

const fmtEur = (n: number) =>
  n < 0.01 ? `€${n.toFixed(4)}` : `€${n.toFixed(3)}`;

const WriterPricingSuggester: React.FC = () => {
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('writer_credit_ledger')
        .select('id, created_at, kind, turns, credit_equivalent, project_id, thread_id, metadata')
        .eq('kind', 'turn_debit')
        .order('created_at', { ascending: false })
        .limit(500);
      if (!cancelled) setRows((data ?? []) as unknown as Row[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const suggestion = useMemo(() => computeSuggestion(rows ?? []), [rows]);

  if (!rows) return null;
  if (!suggestion) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data-driven pricing suggestion</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No turn telemetry recorded yet. Once admins run Writer turns, this panel will
            calculate cost per turn and a suggested price at{' '}
            {Math.round(TARGET_GROSS_MARGIN * 100)}% target gross margin.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Data-driven pricing suggestion</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">
          Based on {suggestion.sampleSize} recorded turn{suggestion.sampleSize === 1 ? '' : 's'}{' '}
          (Gemini 3.5 Flash, EU).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Avg prompt tokens</div>
            <div className="font-mono">{Math.round(suggestion.avgPromptTokens).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Avg completion tokens</div>
            <div className="font-mono">
              {Math.round(suggestion.avgCompletionTokens).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Estimated cost / turn</div>
            <div className="font-mono">{fmtEur(suggestion.costEurPerTurn)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">
              Suggested price / turn @ {Math.round(TARGET_GROSS_MARGIN * 100)}% margin
            </div>
            <div className="font-mono">{fmtEur(suggestion.suggestedPriceEurPerTurn)}</div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground pt-2">
          Adjust `COST_PER_1M_INPUT_USD` / `COST_PER_1M_OUTPUT_USD` /
          `TARGET_GROSS_MARGIN` in the source when Vertex pricing changes.
        </p>
      </CardContent>
    </Card>
  );
};

export default WriterPricingSuggester;
